import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  ProgramBroadcastFiltersState,
  useProgramBroadcastFilters,
} from '@/stores/program-broadcast-filters';
import { endOfAppDay, startOfAppDay } from '@/utils/spain-time';

export const useSyncProgramFilters = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFilters, ...currentFilters } = useProgramBroadcastFilters();
  const isHydratedRef = useRef(false);

  useEffect(() => {
    const stringKeys = [
      'order_by',
      'gte',
      'lte',
      'search',
      'channelType',
      'projectSearch',
      'tagSearch',
      'startDatetimeGte',
      'startDatetimeLte',
      'endDatetimeGte',
      'endDatetimeLte',
    ] as const;

    const arrayKeys = [
      'review_states',
      'time_keeping_states',
      'workspaces',
      'projects',
      'tags',
      'logos',
      'broadcasters',
      'programs',
      'people',
      'clients',
    ] as const;

    type StringKey = (typeof stringKeys)[number];

    const urlFilters: Partial<Pick<ProgramBroadcastFiltersState, StringKey>> = {};

    let hasFilters = false;

    stringKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        urlFilters[key as keyof ProgramBroadcastFiltersState] = value;
        hasFilters = true;
      }
    });

    arrayKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        if (key === 'review_states' || key === 'time_keeping_states') {
          urlFilters[key] = value
            .split(',')
            .map(Number)
            .filter(n => !isNaN(n));
        } else {
          urlFilters[key as keyof ProgramBroadcastFiltersState] = value.split(',');
        }
        hasFilters = true;
      }
    });

    if (hasFilters) {
      setFilters(urlFilters);
    } else {
      // Today as the Madrid day, whatever the viewer's timezone (RCOMM-6264).
      setFilters({
        gte: startOfAppDay().toISOString(),
        lte: endOfAppDay().toISOString(),
      });
    }

    isHydratedRef.current = true;
  }, [location.pathname, searchParams, setFilters]);

  useEffect(() => {
    if (!isHydratedRef.current) return;

    const params = new URLSearchParams(window.location.search);

    const updateParam = (key: string, value: string | undefined | null) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    };

    updateParam('order_by', currentFilters.order_by);
    updateParam('gte', currentFilters.gte);
    updateParam('lte', currentFilters.lte);
    updateParam('search', currentFilters.search);
    updateParam('channelType', currentFilters.channelType);
    updateParam('projectSearch', currentFilters.projectSearch);
    updateParam('tagSearch', currentFilters.tagSearch);
    updateParam('startDatetimeGte', currentFilters.startDatetimeGte);
    updateParam('startDatetimeLte', currentFilters.startDatetimeLte);
    updateParam('endDatetimeGte', currentFilters.endDatetimeGte);
    updateParam('endDatetimeLte', currentFilters.endDatetimeLte);

    if (currentFilters.review_states?.length) {
      params.set('review_states', currentFilters.review_states.join(','));
    } else {
      params.delete('review_states');
    }

    if (currentFilters.time_keeping_states?.length) {
      params.set('time_keeping_states', currentFilters.time_keeping_states.join(','));
    } else {
      params.delete('time_keeping_states');
    }

    const arrayKeys: (keyof typeof currentFilters)[] = [
      'workspaces',
      'projects',
      'tags',
      'logos',
      'broadcasters',
      'programs',
      'people',
      'clients',
    ];

    arrayKeys.forEach(key => {
      const val = currentFilters[key];
      if (Array.isArray(val) && val.length) {
        params.set(key, val.join(','));
      } else {
        params.delete(key);
      }
    });

    setSearchParams(params, { replace: true });
  }, [
    currentFilters.order_by,
    currentFilters.gte,
    currentFilters.lte,
    currentFilters.search,
    currentFilters.channelType,
    currentFilters.projectSearch,
    currentFilters.tagSearch,
    currentFilters.review_states,
    currentFilters.time_keeping_states,
    currentFilters.workspaces,
    currentFilters.projects,
    currentFilters.tags,
    currentFilters.logos,
    currentFilters.broadcasters,
    currentFilters.programs,
    currentFilters.people,
    currentFilters.clients,
    currentFilters.startDatetimeGte,
    currentFilters.endDatetimeLte,
    currentFilters.startDatetimeLte,
    currentFilters.endDatetimeGte,
  ]);
};
